import { useCallback, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { Dog } from './Dog'
import { PawPrints } from './PawPrints'
import { WANDER_MS, prefersReducedMotion } from './timing'

/**
 * How long between cameos, in milliseconds.
 *
 * Short enough that a guest reading the page will see him without waiting for
 * him, and randomised so it never turns into a metronome.
 */
const MIN_GAP = 5_000
const MAX_GAP = 15_000

/**
 * How far above the bottom edge the ground line sits.
 *
 * The artwork leaves a few units of space below his feet, so sitting the box
 * flush on the bottom already floats him slightly; this is just enough to keep
 * the prints from being clipped in half by the edge.
 */
const GROUND_INSET = 6

/** Matches the sweep in the dog-wander-* keyframes. */
const OFF_RIGHT = 1.1
const OFF_LEFT = -0.4

export type Direction = 'left' | 'right'

interface WanderingDogProps {
  /**
   * Called as each cameo begins, with the way he is going.
   *
   * Purely so something else can come on behind him - see WanderingTerrier. It
   * fires at the start rather than the end because following somebody means being
   * on screen while they still are; waiting for him to leave just produced a
   * second dog on an empty page. Nothing about his own behaviour depends on it.
   */
  onSetOff?: (direction: Direction) => void
}

/**
 * Every so often the dog wanders across the bottom of the page and off again,
 * in one direction or the other.
 *
 * No letter, no interaction - he is just passing. Lives in the ambient layer
 * rather than the submit sequence, and reuses the same artwork and clip rig, so
 * the legs and tail animate exactly as they do when he is working, and he
 * leaves the same prints.
 *
 * The gap and the direction are both randomised, so it never becomes a
 * metronome. Nothing runs at all under reduced motion.
 */
export function WanderingDog({ onSetOff }: WanderingDogProps = {}) {
  const [direction, setDirection] = useState<Direction | null>(null)

  const gap = useCallback(() => MIN_GAP + Math.random() * (MAX_GAP - MIN_GAP), [])

  useEffect(() => {
    if (prefersReducedMotion()) return

    let timer = 0
    let cancelled = false

    const schedule = (delay: number) => {
      timer = window.setTimeout(() => {
        if (cancelled) return
        const heading = Math.random() < 0.5 ? 'left' : 'right'
        setDirection(heading)
        onSetOff?.(heading)
        timer = window.setTimeout(() => {
          if (cancelled) return
          setDirection(null)
          schedule(gap())
        }, WANDER_MS)
      }, delay)
    }

    // Not immediately: let the guest read their invite first.
    schedule(gap())

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [gap, onSetOff])

  if (direction === null) return null

  const goingLeft = direction === 'left'
  const from = window.innerWidth * (goingLeft ? OFF_RIGHT : OFF_LEFT)
  const to = window.innerWidth * (goingLeft ? OFF_LEFT : OFF_RIGHT)

  return (
    <>
      {/* Prints stay put on the ground while he moves over them, so they are a
          sibling of the travelling element rather than a child of it. They work
          out which way to face from `from` and `to`. */}
      <div className="absolute" style={{ bottom: GROUND_INSET }}>
        <PawPrints
          // Remounts on each cameo, so the delays restart rather than carrying
          // over from the last one.
          key={`${direction}-${from}`}
          from={from}
          to={to}
          travelMs={WANDER_MS}
          count={16}
          lifeMs={2600}
        />
      </div>

      <div
        className={`absolute ${goingLeft ? 'animate-dog-wander-left' : 'animate-dog-wander-right'}`}
        style={{ bottom: 0, '--wander-duration': `${WANDER_MS}ms` } as CSSProperties}
      >
        <div className="animate-dog-bob">
          {/* The artwork faces left, so going right is a mirror. */}
          <div style={goingLeft ? undefined : { transform: 'scaleX(-1)' }}>
            <Dog className="h-24 w-28 opacity-90 drop-shadow-sm" />
          </div>
        </div>
      </div>
    </>
  )
}
